import clsx from 'clsx';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { CurrentUserPlanContainer } from '@/components/Containers/CurrentUserPlanContainer';
import { HelpContainer } from '@/components/Elements';
import { OldPlanUserAdvise } from '@/components/Elements/OldPlanUserAdvise';
import { ProductCard } from '@/components/Elements/ProductCard';
import { NOTIFICATION_CHECKOUT_REMINDER_ID } from '@/config';
import { ACTIVE, CANCELLED, INCOMPLETE, PENDING, PROCESSING } from '@/config/appConstants';
import { useNotificationStore } from '@/stores/notifications';
import { Currency, UserData, ProductTypes, StatusType, UserProductTypes } from '@/types';
import {
  findPromotionCodeByGroupProductAndProduct,
  getBillingModeFromUserProduct,
  haveUserAnOldPlan,
  isNewFreeUser,
} from '@/utils/helpers';

export type MyRatePageContainerProps = {
  userData: UserData;
  userProducts: UserProductTypes[];
  userCurrency: Currency;
  onRequestInfo: (product: ProductTypes | null) => void;
  onProductSelect: (product: ProductTypes) => void;
  onPlanCancel: (product: ProductTypes | null | undefined) => void;
  onPlanReactivate: (product: ProductTypes | null | undefined) => void;
  onAppsumoAction: () => void;
  mdirectorId: string | null;
  onPendingButtonAction?: (product: ProductTypes) => void;
  onIncompleteAction?: () => void;
  onCancelIncompleteAction?: () => void;
};

export const MyRatePageContainer = ({
  userData,
  userProducts,
  userCurrency,
  onRequestInfo,
  onProductSelect,
  onPlanCancel,
  onPlanReactivate,
  onAppsumoAction,
  mdirectorId,
  onPendingButtonAction,
  onIncompleteAction,
  onCancelIncompleteAction,
}: MyRatePageContainerProps) => {
  const { t } = useTranslation();
  const { dismissNotification } = useNotificationStore();

  const onDefaultRequestInfoOpen = () => {
    onRequestInfo && onRequestInfo(null);
  };

  const onDefaultCancelIncompleteAction = async () => {
    onCancelIncompleteAction && (await onCancelIncompleteAction());
  };
  const parseUserProduct = (userProduct: UserProductTypes) => {
    userProduct.product.featured = false;
    return userProduct.product;
  };

  const isUserProductInStatus = (userProduct: UserProductTypes, status: StatusType): boolean =>
    userProduct.groupProduct.status === status;

  userProducts = userProducts.sort((userProduct: UserProductTypes) =>
    isUserProductInStatus(userProduct, PENDING) || isUserProductInStatus(userProduct, INCOMPLETE)
      ? -1
      : 1
  );

  if (haveUserAnOldPlan(userProducts)) {
    return (
      <section>
        <h2 className="md__payments__title--h2">{t('MyRatePage.title')}</h2>
        <OldPlanUserAdvise />
        <HelpContainer onClick={onDefaultRequestInfoOpen} className="mt-5" />
      </section>
    );
  }

  const onDefaultProductSelect = (product: ProductTypes) => {
    dismissNotification(`${NOTIFICATION_CHECKOUT_REMINDER_ID}${product?.mdirectorId}`);
    onProductSelect(product);
  };

  const checkIfAutoCheckout = (userProduct: UserProductTypes) => {
    if (isNewFreeUser(userData)) return false;

    if (
      userProduct.product.mdirectorId === mdirectorId &&
      (isUserProductInStatus(userProduct, ACTIVE) ||
        isUserProductInStatus(userProduct, PROCESSING) ||
        isUserProductInStatus(userProduct, CANCELLED))
    )
      return false;

    return (
      isUserProductInStatus(userProduct, PENDING) && userProduct.product.mdirectorId === mdirectorId
    );
  };

  return (
    <section>
      <h2 className="md__payments__title--h2">{t('MyRatePage.title')}</h2>
      <div className="flex items-center items-stretch flex-wrap mb-20">
        {haveUserAnOldPlan(userProducts) ? (
          <OldPlanUserAdvise className="mt-[35px]" />
        ) : (
          <CurrentUserPlanContainer
            userCurrency={userCurrency}
            onPlanCancel={onPlanCancel}
            onPlanReactivate={onPlanReactivate}
            onAppsumoAction={onAppsumoAction}
            onRequestInfo={onDefaultRequestInfoOpen}
            productType="email"
            userProducts={userProducts}
            onCancelIncompleteAction={onDefaultCancelIncompleteAction}
          />
        )}

        <div className="p-1 w-full md:w-1/2 xl:w-1/4 pt-8 pr-8">
          {userProducts.map((userProduct: UserProductTypes) => {
            const activeBillingMode = isUserProductInStatus(userProduct, ACTIVE)
              ? getBillingModeFromUserProduct(userProduct)
              : null;
            const productParsed = parseUserProduct(userProduct);
            if (isUserProductInStatus(userProduct, PROCESSING)) return;
            return (
              <ProductCard
                key={userProduct.product.id}
                product={productParsed}
                className={clsx('w-full border-2 border-md-blue rounded-lg', {
                  'mb-5': userProducts.length > 0,
                })}
                userCurrency={userCurrency}
                alreadyPurchased={
                  isUserProductInStatus(userProduct, ACTIVE) ||
                  isUserProductInStatus(userProduct, CANCELLED)
                }
                removePrice={true}
                promotionCode={findPromotionCodeByGroupProductAndProduct(
                  userProducts,
                  userProduct.product
                )}
                isCancelled={isUserProductInStatus(userProduct, CANCELLED)}
                currentPeriodEndDate={userProduct.groupProduct.currentPeriodEndDate}
                isProcessing={isUserProductInStatus(userProduct, PROCESSING)}
                onProductSelect={onDefaultProductSelect}
                actionButtonLabel={
                  isUserProductInStatus(userProduct, PENDING) ? t('MyRatePage.completePayment') : ''
                }
                autoCheckout={checkIfAutoCheckout(userProduct)}
                activeBillingMode={activeBillingMode}
                userBillingMode={getBillingModeFromUserProduct(userProduct)}
                onPendingButtonAction={onPendingButtonAction}
                isIncomplete={isUserProductInStatus(userProduct, INCOMPLETE)}
                onIncompleteAction={onIncompleteAction}
              />
            );
          })}
        </div>

        {haveUserAnOldPlan(userProducts) && (
          <HelpContainer onClick={onDefaultRequestInfoOpen} className="mt-5" />
        )}
      </div>
    </section>
  );
};
